import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/features/auth/hooks/useAuth";
import type {
  CreateStaffMemberInput,
  StaffMember,
  UpdateStaffMemberInput,
} from "@/features/staff/schemas/staff";
import {
  createStaffMember,
  deleteStaffMember,
  getStaffMember,
  listStaffMembers,
  provisionStaffMember,
  updateStaffMember,
  type ProvisionStaffMemberInput,
} from "@/features/staff/services/staffService";
import {
  findLegacyAdminAccounts,
  migrateAccountsToStaff,
  type StaffMigrationCandidate,
} from "@/features/staff/services/staffMigration";

export const staffKeys = {
  all: ["staff"] as const,
  list: () => [...staffKeys.all, "list"] as const,
  member: (uid: string | null) => [...staffKeys.all, "member", uid] as const,
  migration: () => [...staffKeys.all, "migration"] as const,
};

const STAFF_STALE_TIME = 5 * 60 * 1000;

export function useStaffMembers() {
  return useQuery<StaffMember[]>({
    queryKey: staffKeys.list(),
    queryFn: listStaffMembers,
    staleTime: STAFF_STALE_TIME,
  });
}

export function useStaffMember(uid: string | null) {
  return useQuery<StaffMember | null>({
    queryKey: staffKeys.member(uid),
    queryFn: () => (uid ? getStaffMember(uid) : Promise.resolve(null)),
    enabled: uid !== null,
    staleTime: STAFF_STALE_TIME,
  });
}

export function useCreateStaffMember() {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();

  return useMutation({
    mutationFn: (input: CreateStaffMemberInput) =>
      createStaffMember(input, currentUser?.uid ?? null),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: staffKeys.all });
    },
  });
}

/** Alta completa: crea la cuenta de acceso y el perfil de equipo. */
export function useProvisionStaffMember() {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();

  return useMutation({
    mutationFn: (input: ProvisionStaffMemberInput) =>
      provisionStaffMember(input, currentUser?.uid ?? null),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: staffKeys.all });
    },
  });
}

export function useUpdateStaffMember() {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();

  return useMutation({
    mutationFn: ({ uid, data }: { uid: string; data: UpdateStaffMemberInput }) =>
      updateStaffMember(uid, data, currentUser?.uid ?? null),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: staffKeys.all });
    },
  });
}

export function useDeleteStaffMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uid: string) => deleteStaffMember(uid),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: staffKeys.all });
    },
  });
}

/** Cuentas que hoy entran por el gate antiguo (`users.vm_admin`). */
export function useLegacyAdminAccounts(enabled = true) {
  return useQuery<StaffMigrationCandidate[]>({
    queryKey: staffKeys.migration(),
    queryFn: findLegacyAdminAccounts,
    enabled,
    staleTime: 0,
  });
}

export function useMigrateAccountsToStaff() {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();

  return useMutation({
    mutationFn: (candidates: readonly StaffMigrationCandidate[]) =>
      migrateAccountsToStaff(candidates, currentUser?.uid ?? null),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: staffKeys.all });
    },
  });
}
